// CreditNova Extraction Schemas
// Used with Vercel AI SDK's generateObject() for structured output
import { z } from 'zod';

// ===========================================
// Common Enums
// ===========================================

export const detailLevelSchema = z.enum(['high-level', 'detailed', 'in-depth']);
export type DetailLevel = z.infer<typeof detailLevelSchema>;

export const riskScoreSchema = z.enum(['green', 'yellow', 'red']);
export type RiskScore = z.infer<typeof riskScoreSchema>;

// ===========================================
// Document Metadata Schema
// ===========================================

export const documentMetadataSchema = z.object({
  companyName: z.string().nullable(), // nullable instead of optional for OpenAI strict mode
  availablePeriods: z.array(z.string()), // e.g., ["2024", "2023"]
  hasIncomeStatement: z.boolean(),
  hasBalanceSheet: z.boolean(),
  hasCashFlowStatement: z.boolean(),
  hasNotes: z.boolean(),
});

export type DocumentMetadata = z.infer<typeof documentMetadataSchema>;

// ===========================================
// Financial Statement Extraction Schemas
// ===========================================

// ===========================================
// Note Content Block Schemas (for mixed text/table content)
// ===========================================

// Table cell structure (from Azure Doc Intelligence)
export const tableCellSchema = z.object({
  content: z.string(),
  rowIndex: z.number(),
  columnIndex: z.number(),
});

export type TableCell = z.infer<typeof tableCellSchema>;

// Table structure for notes
export const noteTableSchema = z.object({
  rowCount: z.number(),
  columnCount: z.number(),
  cells: z.array(tableCellSchema),
});

export type NoteTable = z.infer<typeof noteTableSchema>;

// Text content block
export const textBlockSchema = z.object({
  type: z.literal('text'),
  value: z.string(),
});

export type TextBlock = z.infer<typeof textBlockSchema>;

// Table content block
export const tableBlockSchema = z.object({
  type: z.literal('table'),
  value: noteTableSchema,
});

export type TableBlock = z.infer<typeof tableBlockSchema>;

// Discriminated union for content blocks
export const contentBlockSchema = z.discriminatedUnion('type', [
  textBlockSchema,
  tableBlockSchema,
]);

export type ContentBlock = z.infer<typeof contentBlockSchema>;

// Note entry for in-depth analysis
export const noteSchema = z.object({
  lineItem: z.string().nullable(), // e.g., "Revenue (note 2)", "Goodwill (Note 7)"
  noteReference: z.string().nullable(), // e.g., "Note 3", "See Note 12"
  noteTitle: z.string().nullable(), // e.g., "Summary of Significant Accounting Policies"
  // NEW: Ordered content blocks preserving text/table interleaving
  content: z.array(contentBlockSchema).nullable(),
  // LEGACY: Plain text description (fallback when content blocks not available)
  description: z.string().nullable(),
  pageNumbers: z.array(z.number()).nullable(),
});

export type Note = z.infer<typeof noteSchema>;

// Dynamic line items schema - allows any string keys with string values
export const lineItemsSchema = z.record(z.string(), z.string());

// Financial data with dynamic line items structure
export const dynamicFinancialDataSchema = z.object({
  companyName: z.string().nullish(),
  incomeStatement: lineItemsSchema.nullable(),
  balanceSheet: lineItemsSchema.nullable(),
  cashFlow: lineItemsSchema.nullable(),
  notes: z.array(noteSchema).nullable(),
});

export type DynamicFinancialData = z.infer<typeof dynamicFinancialDataSchema>;

// Standardized fields for deterministic ratio calculations.
// These are extracted by OpenAI in a dedicated stage and contain plain numeric
// values (no currency symbols) mapped to canonical field names.
export const standardizedFieldsSchema = z.object({
  // Balance Sheet - Assets
  currentAssets: z.string().nullable().optional(),
  totalAssets: z.string().nullable().optional(),
  cash: z.string().nullable().optional(),
  accountsReceivable: z.string().nullable().optional(),
  inventory: z.string().nullable().optional(),
  prepaidExpenses: z.string().nullable().optional(),
  propertyPlantEquipment: z.string().nullable().optional(),
  intangibles: z.string().nullable().optional(),
  // Balance Sheet - Liabilities
  currentLiabilities: z.string().nullable().optional(),
  totalLiabilities: z.string().nullable().optional(),
  accountsPayable: z.string().nullable().optional(),
  shortTermDebt: z.string().nullable().optional(),
  longTermDebt: z.string().nullable().optional(),
  totalDebt: z.string().nullable().optional(),
  // Balance Sheet - Equity
  totalEquity: z.string().nullable().optional(),
  retainedEarnings: z.string().nullable().optional(),
  // Income Statement
  revenue: z.string().nullable().optional(),
  costOfGoodsSold: z.string().nullable().optional(),
  grossProfit: z.string().nullable().optional(),
  operatingExpenses: z.string().nullable().optional(),
  operatingIncome: z.string().nullable().optional(),
  interestExpense: z.string().nullable().optional(),
  netIncome: z.string().nullable().optional(),
  depreciation: z.string().nullable().optional(),
  amortization: z.string().nullable().optional(),
  ebitda: z.string().nullable().optional(),
});

export type StandardizedFields = z.infer<typeof standardizedFieldsSchema>;

// Combined financial data response
export const financialDataSchema = z.object({
  periodEnding: z.string().nullable(),
  companyName: z.string().nullable(),
  detailLevel: detailLevelSchema,
  data: dynamicFinancialDataSchema,
  // Optional: pre-extracted standardized fields for ratio calculations.
  // When present, calculateDeterministicRatios can skip its own OpenAI call.
  standardizedFields: standardizedFieldsSchema.optional(),
});

export type FinancialData = z.infer<typeof financialDataSchema>;

// ===========================================
// Equifax/Credit Report Extraction Schemas
// ===========================================

export const creditReportProviderInfoSchema = z.object({
  providerName: z.string().nullable(),
  reportType: z.string().nullable(),
  reportDate: z.string().nullable(),
  reportId: z.string().nullable(),
});
export type CreditReportProviderInfo = z.infer<typeof creditReportProviderInfoSchema>;

export const creditReportDataContentSchema = z.object({
  personalInfo: lineItemsSchema.nullable(),
  creditSummary: lineItemsSchema.nullable(),
  accountHistory: z.array(lineItemsSchema).nullable(),
  inquiries: z.array(lineItemsSchema).nullable(),
  publicRecords: z.array(lineItemsSchema).nullable(),
});

export type CreditReportDataContent = z.infer<typeof creditReportDataContentSchema>;

export const creditReportExtractedDataSchema = z.object({
  reportDate: z.string().nullable(),
  subjectName: z.string().nullable(),
  providerInfo: creditReportProviderInfoSchema.nullable(),
  data: creditReportDataContentSchema,
});

export type CreditReportExtractedData = z.infer<typeof creditReportExtractedDataSchema>;

// Selected Credit Report items schema (for report generation)
export const selectedCreditReportItemsSchema = z.object({
  personalInfo: z.array(z.string()),
  creditSummary: z.array(z.string()),
  accountHistory: z.array(z.number()), // Array indices
  inquiries: z.array(z.number()), // Array indices
  publicRecords: z.array(z.number()), // Array indices
});

export type SelectedCreditReportItems = z.infer<typeof selectedCreditReportItemsSchema>;

// ===========================================
// Corporate Search Extraction Schema
// ===========================================

export const accountDataSchema = z.object({
  yearOfIncorporation: z.string().nullable(),
  ownershipStructure: z.string().nullable(),
  operatingArea: z.string().nullable(),
  numberOfLocations: z.string().nullable(),
  managementExperience: z.string().nullable(),
  servicesProvided: z.string().nullable(),
  industriesServed: z.string().nullable(),
  mainCustomers: z.string().nullable(),
  description: z.string().nullable(),
  companyName: z.string().nullable(),
  providerName: z.string().nullable(),
  reportType: z.string().nullable(),
  additionalFields: z.record(z.string(), z.string()).nullable(),
});

export type AccountData = z.infer<typeof accountDataSchema>;

// ===========================================
// Bank Statement Metadata Schema
// ===========================================

export const bankStatementTypeSchema = z.enum(['individual', 'business', 'dynamic_capital']);
export type BankStatementType = z.infer<typeof bankStatementTypeSchema>;

export const bankStatementUploadSchema = z.object({
  accountHolder: z.string().nullable(),
  institution: z.string().nullable(),
  statementPeriod: z.string(), // e.g., "January 2024"
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  totalDeposits: z.string().nullable(),
  totalWithdrawals: z.string().nullable(),
  openingBalance: z.string().nullable(),
  closingBalance: z.string().nullable(),
  type: bankStatementTypeSchema, // Required for OpenAI strict mode
});

export type BankStatementUpload = z.infer<typeof bankStatementUploadSchema>;

// ===========================================
// Individual Bank Statement Findings (10 Categories)
// ===========================================

// 1. Income Verification
export const incomeVerificationSchema = z.object({
  consistencyScore: riskScoreSchema,
  regularPayroll: z.boolean(),
  payrollFrequency: z.string().nullable(), // e.g., "bi-weekly", "monthly"
  averageMonthlyIncome: z.string(),
  incomeVariability: z.string(), // e.g., "Low", "Medium", "High"
  incomeSources: z.array(z.string()), // e.g., ["Payroll - ABC Corp", "Rental Income"]
  qualitativeSummary: z.string(),
  flaggedIssues: z.array(z.string()).nullable(),
  sourceReferences: z.array(z.string()).nullable(),
});

export type IncomeVerification = z.infer<typeof incomeVerificationSchema>;

// 2. Expense Analysis
export const expenseAnalysisSchema = z.object({
  riskScore: riskScoreSchema,
  averageMonthlyExpenses: z.string(),
  fixedObligations: z.array(z.object({
    category: z.string(),
    amount: z.string(),
  })),
  variableSpending: z.string(),
  debtRepayments: z.array(z.object({
    creditor: z.string(),
    amount: z.string(),
  })),
  qualitativeSummary: z.string(),
  flaggedIssues: z.array(z.string()).nullable(),
  sourceReferences: z.array(z.string()).nullable(),
});

export type ExpenseAnalysis = z.infer<typeof expenseAnalysisSchema>;

// 3. Cash Flow Health
export const cashFlowHealthSchema = z.object({
  riskScore: riskScoreSchema,
  netCashFlow: z.string(),
  cashFlowTrend: z.string(),
  averageEndOfMonthBalance: z.string(),
  balanceVolatility: z.string(),
  unexpectedMovements: z.array(z.object({
    date: z.string(),
    amount: z.string(),
    description: z.string(),
  })).nullable(),
  qualitativeSummary: z.string(),
  flaggedIssues: z.array(z.string()).nullable(),
  sourceReferences: z.array(z.string()).nullable(),
});

export type CashFlowHealth = z.infer<typeof cashFlowHealthSchema>;

// 4. Overdraft and NSF Activity
export const overdraftNSFSchema = z.object({
  riskScore: riskScoreSchema,
  overdraftFrequency: z.number(),
  totalOverdraftFees: z.string(),
  nsfCount: z.number(),
  totalNSFFees: z.string(),
  chronicOverdraftPattern: z.boolean(),
  qualitativeSummary: z.string(),
  flaggedIssues: z.array(z.string()).nullable(),
  sourceReferences: z.array(z.string()).nullable(),
});

export type OverdraftNSF = z.infer<typeof overdraftNSFSchema>;

// 5. Debt Repayment Behavior
export const debtRepaymentSchema = z.object({
  riskScore: riskScoreSchema,
  timelyPayments: z.boolean(),
  minimumPaymentsOnly: z.boolean(),
  evidenceOfPaydayLenders: z.boolean(),
  thirdPartyFinancingApps: z.array(z.string()).nullable(),
  paymentHistory: z.array(z.object({
    creditor: z.string(),
    amount: z.string(),
    onTime: z.boolean(),
  })).nullable(),
  qualitativeSummary: z.string(),
  flaggedIssues: z.array(z.string()).nullable(),
  sourceReferences: z.array(z.string()).nullable(),
});

export type DebtRepayment = z.infer<typeof debtRepaymentSchema>;

// 6. Incoming Transfers / Other Liabilities
export const incomingTransfersSchema = z.object({
  riskScore: riskScoreSchema,
  unusualTransferPatterns: z.boolean(),
  peerToPeerActivity: z.array(z.object({
    platform: z.string(),
    frequency: z.string(),
    averageAmount: z.string(),
  })).nullable(),
  undisclosedIncome: z.string().nullable(),
  qualitativeSummary: z.string(),
  flaggedIssues: z.array(z.string()).nullable(),
  sourceReferences: z.array(z.string()).nullable(),
});

export type IncomingTransfers = z.infer<typeof incomingTransfersSchema>;

// 7. Employer or Business Validation
export const employerValidationSchema = z.object({
  riskScore: riskScoreSchema,
  employerName: z.string().nullable(),
  payrollSource: z.string().nullable(),
  matchesDeclared: z.boolean(),
  businessConsistency: z.string().nullable(),
  qualitativeSummary: z.string(),
  flaggedIssues: z.array(z.string()).nullable(),
  sourceReferences: z.array(z.string()).nullable(),
});

export type EmployerValidation = z.infer<typeof employerValidationSchema>;

// 8. Seasonality / Stability Trends
export const seasonalityTrendsSchema = z.object({
  riskScore: riskScoreSchema,
  incomeTrend: z.string(),
  seasonalPattern: z.boolean(),
  monthlyIncomeData: z.array(z.object({
    month: z.string(),
    income: z.string(),
  })).nullable(),
  sustainability: z.string(),
  qualitativeSummary: z.string(),
  flaggedIssues: z.array(z.string()).nullable(),
  sourceReferences: z.array(z.string()).nullable(),
});

export type SeasonalityTrends = z.infer<typeof seasonalityTrendsSchema>;

// 9. Lifestyle & Risk Indicators
export const lifestyleRiskSchema = z.object({
  riskScore: riskScoreSchema,
  gamblingActivity: z.boolean(),
  gamblingAmount: z.string().nullable(),
  highDiscretionarySpending: z.boolean(),
  discretionarySpendingRatio: z.string().nullable(),
  frequentCashWithdrawals: z.boolean(),
  averageCashWithdrawal: z.string().nullable(),
  qualitativeSummary: z.string(),
  flaggedIssues: z.array(z.string()).nullable(),
  sourceReferences: z.array(z.string()).nullable(),
});

export type LifestyleRisk = z.infer<typeof lifestyleRiskSchema>;

// 10. Verification of Declared Assets and Accounts
export const assetVerificationSchema = z.object({
  riskScore: riskScoreSchema,
  accountOwnershipConfirmed: z.boolean(),
  accountMatchesBorrower: z.boolean(),
  additionalAccountsFound: z.number(),
  discrepancies: z.array(z.string()).nullable(),
  qualitativeSummary: z.string(),
  flaggedIssues: z.array(z.string()).nullable(),
  sourceReferences: z.array(z.string()).nullable(),
});

export type AssetVerification = z.infer<typeof assetVerificationSchema>;

// Complete individual bank statement findings
export const bankStatementFindingsSchema = z.object({
  uploadId: z.string(),
  statementPeriods: z.array(z.string()),
  accountInfo: bankStatementUploadSchema,
  findings: z.object({
    incomeVerification: incomeVerificationSchema,
    expenseAnalysis: expenseAnalysisSchema,
    cashFlowHealth: cashFlowHealthSchema,
    overdraftNSF: overdraftNSFSchema,
    debtRepayment: debtRepaymentSchema,
    incomingTransfers: incomingTransfersSchema,
    employerValidation: employerValidationSchema,
    seasonalityTrends: seasonalityTrendsSchema,
    lifestyleRisk: lifestyleRiskSchema,
    assetVerification: assetVerificationSchema,
  }),
  overallRiskScore: riskScoreSchema,
  executiveSummary: z.string(),
  extractedDate: z.string(),
});

export type BankStatementFindings = z.infer<typeof bankStatementFindingsSchema>;

// ===========================================
// Business Bank Statement Findings (13 Categories)
// ===========================================

// Evidence structure for traceability
export const evidenceSchema = z.object({
  page: z.number().nullable(),
  textSnippet: z.string().nullable(),
  evidenceStatus: z.enum(['exact_match', 'derived_from_rows', 'not_found']).nullable(),
});

export type Evidence = z.infer<typeof evidenceSchema>;

// 1. Revenue (Deposits)
export const businessRevenueSchema = z.object({
  totalDeposits: z.object({
    value: z.string().nullable(),
    evidence: evidenceSchema.nullable(),
  }).nullable(),
  monthlyDeposits: z.array(z.object({
    month: z.string(),
    sum: z.string().nullable(),
    count: z.number().nullable(),
  })).nullable(),
  topDepositSources: z.array(z.object({
    name: z.string().nullable(),
    sum: z.string().nullable(),
    share: z.string().nullable(),
  })).nullable(),
  qualitativeSummary: z.string(),
  flaggedIssues: z.array(z.string()).nullable(),
});

export type BusinessRevenue = z.infer<typeof businessRevenueSchema>;

// 2. Expense Management (Debits)
export const businessExpenseSchema = z.object({
  totalDebits: z.object({
    value: z.string().nullable(),
    evidence: evidenceSchema.nullable(),
  }).nullable(),
  monthlyDebits: z.array(z.object({
    month: z.string(),
    sum: z.string().nullable(),
  })).nullable(),
  fixedExpenses: z.array(z.object({
    type: z.string(),
    sum: z.string().nullable(),
  })).nullable(),
  variableExpenses: z.array(z.object({
    label: z.string(),
    sum: z.string().nullable(),
  })).nullable(),
  qualitativeSummary: z.string(),
  flaggedIssues: z.array(z.string()).nullable(),
});

export type BusinessExpense = z.infer<typeof businessExpenseSchema>;

// 3. Business Cash Flow Health
export const businessCashFlowSchema = z.object({
  netCashFlow: z.object({
    value: z.string().nullable(),
    evidence: evidenceSchema.nullable(),
  }).nullable(),
  monthEndBalances: z.array(z.object({
    date: z.string(),
    balance: z.string().nullable(),
  })).nullable(),
  avgDailyBalance: z.string().nullable(),
  daysNegativeBalance: z.number().nullable(),
  qualitativeSummary: z.string(),
  flaggedIssues: z.array(z.string()).nullable(),
});

export type BusinessCashFlow = z.infer<typeof businessCashFlowSchema>;

// 4. Business Overdrafts & NSFs
export const businessOverdraftSchema = z.object({
  nsfEventsCount: z.number().nullable(),
  overdraftDaysCount: z.number().nullable(),
  overdraftFeesTotal: z.string().nullable(),
  qualitativeSummary: z.string(),
  flaggedIssues: z.array(z.string()).nullable(),
});

export type BusinessOverdraft = z.infer<typeof businessOverdraftSchema>;

// 5. Debt Servicing & Loan Repayments
export const businessDebtServiceSchema = z.object({
  loanPayments: z.array(z.object({
    lender: z.string().nullable(),
    amount: z.string().nullable(),
    cadence: z.string().nullable(),
  })).nullable(),
  totalLoanPayments: z.string().nullable(),
  qualitativeSummary: z.string(),
  flaggedIssues: z.array(z.string()).nullable(),
});

export type BusinessDebtService = z.infer<typeof businessDebtServiceSchema>;

// 6. Supplier & Vendor Payments
export const businessVendorSchema = z.object({
  keyVendors: z.array(z.object({
    name: z.string().nullable(),
    sum: z.string().nullable(),
    count: z.number().nullable(),
    recurrence: z.string().nullable(),
  })).nullable(),
  concentrationRisk: z.string().nullable(),
  qualitativeSummary: z.string(),
  flaggedIssues: z.array(z.string()).nullable(),
});

export type BusinessVendor = z.infer<typeof businessVendorSchema>;

// 7. Receivables & Customer Base
export const businessReceivablesSchema = z.object({
  arTurnoverApprox: z.string().nullable(),
  concentratedCustomers: z.array(z.object({
    name: z.string().nullable(),
    share: z.string().nullable(),
  })).nullable(),
  customerCollectionRisk: z.string().nullable(),
  qualitativeSummary: z.string(),
  flaggedIssues: z.array(z.string()).nullable(),
});

export type BusinessReceivables = z.infer<typeof businessReceivablesSchema>;

// 8. Owner/Related Party Transactions
export const businessRelatedPartySchema = z.object({
  ownerWithdrawals: z.string().nullable(),
  relatedPartyPayments: z.array(z.object({
    name: z.string().nullable(),
    sum: z.string().nullable(),
  })).nullable(),
  concernsRaised: z.boolean(),
  qualitativeSummary: z.string(),
  flaggedIssues: z.array(z.string()).nullable(),
});

export type BusinessRelatedParty = z.infer<typeof businessRelatedPartySchema>;

// 9. Revenue Trends
export const businessRevenueTrendsSchema = z.object({
  growthTrend: z.string(),
  monthOverMonthChange: z.string().nullable(),
  cyclicality: z.string().nullable(),
  qualitativeSummary: z.string(),
  flaggedIssues: z.array(z.string()).nullable(),
});

export type BusinessRevenueTrends = z.infer<typeof businessRevenueTrendsSchema>;

// 10. Red Flags & Reconciliation
export const businessRedFlagsSchema = z.object({
  suspiciousActivity: z.array(z.string()),
  unclearTransactions: z.array(z.object({
    date: z.string().nullable(),
    amount: z.string().nullable(),
    note: z.string(),
  })).nullable(),
  balanceDiscrepancies: z.boolean(),
  qualitativeSummary: z.string(),
  flaggedIssues: z.array(z.string()).nullable(),
});

export type BusinessRedFlags = z.infer<typeof businessRedFlagsSchema>;

// 11. Operating Efficiency
export const businessOperatingEfficiencySchema = z.object({
  revenuePerDeposit: z.string().nullable(),
  expenseRatio: z.string().nullable(),
  burnRateIfApplicable: z.string().nullable(),
  qualitativeSummary: z.string(),
  flaggedIssues: z.array(z.string()).nullable(),
});

export type BusinessOperatingEfficiency = z.infer<typeof businessOperatingEfficiencySchema>;

// 12. Tax Compliance & Withholdings
export const businessTaxComplianceSchema = z.object({
  regularTaxPayments: z.boolean(),
  payrollTaxesObserved: z.boolean(),
  salesTaxRemitted: z.boolean(),
  qualitativeSummary: z.string(),
  flaggedIssues: z.array(z.string()).nullable(),
});

export type BusinessTaxCompliance = z.infer<typeof businessTaxComplianceSchema>;

// 13. Overall Sustainability
export const businessSustainabilitySchema = z.object({
  overallRisk: z.string(),
  strengthsObserved: z.array(z.string()),
  weaknessesObserved: z.array(z.string()),
  finalRecommendations: z.string(),
  qualitativeSummary: z.string(),
  flaggedIssues: z.array(z.string()).nullable(),
});

export type BusinessSustainability = z.infer<typeof businessSustainabilitySchema>;

// Complete business bank statement findings
export const businessBankStatementFindingsSchema = z.object({
  uploadId: z.string(),
  statementPeriods: z.array(z.string()),
  accountInfo: bankStatementUploadSchema,
  findings: z.object({
    revenue: businessRevenueSchema,
    expenseManagement: businessExpenseSchema,
    cashFlowHealth: businessCashFlowSchema,
    overdraftsNSF: businessOverdraftSchema,
    debtServicing: businessDebtServiceSchema,
    supplierVendor: businessVendorSchema,
    receivables: businessReceivablesSchema,
    relatedParty: businessRelatedPartySchema,
    revenueTrends: businessRevenueTrendsSchema,
    redFlags: businessRedFlagsSchema,
    operatingEfficiency: businessOperatingEfficiencySchema,
    taxCompliance: businessTaxComplianceSchema,
    sustainability: businessSustainabilitySchema,
  }),
  summary: z.object({
    overallRisk: z.string(),
    keyRiskFactors: z.array(z.string()),
    recommendations: z.array(z.string()),
  }).nullable(),
  overallRiskScore: riskScoreSchema,
  executiveSummary: z.string(),
  extractedDate: z.string(),
});

export type BusinessBankStatementFindings = z.infer<typeof businessBankStatementFindingsSchema>;

// ===========================================
// Dynamic Capital Bank Statement Findings (4 Categories)
// ===========================================

// 1. Monthly Deposits Analysis
export const dynamicCapitalDepositsSchema = z.object({
  averageMonthlyDeposits: z.string(),
  monthlyDepositBreakdown: z.array(z.object({
    month: z.string(),
    totalAmount: z.string(),
  })),
  customerList: z.array(z.string()),
  customerVerification: z.string(),
  estimatedAnnualRevenue: z.string(),
  depositConsistency: z.string(),
  depositDetails: z.array(z.object({
    date: z.string(),
    amount: z.string(),
    description: z.string(),
  })).nullable(),
  riskScore: riskScoreSchema,
  summary: z.string(),
  flaggedIssues: z.array(z.string()).nullable(),
});

export type DynamicCapitalDeposits = z.infer<typeof dynamicCapitalDepositsSchema>;

// 2. Liquidity Analysis
export const dynamicCapitalLiquiditySchema = z.object({
  averageBalance: z.string(),
  minimumBalance: z.string().nullable(),
  maximumBalance: z.string().nullable(),
  balanceVolatility: z.string(),
  liquidityAssessment: z.string(),
  riskScore: riskScoreSchema,
  summary: z.string(),
  flaggedIssues: z.array(z.string()).nullable(),
});

export type DynamicCapitalLiquidity = z.infer<typeof dynamicCapitalLiquiditySchema>;

// 3. NSF Activity Analysis
export const dynamicCapitalNSFSchema = z.object({
  nsfCount: z.number().nullable(),
  nsfDetails: z.array(z.object({
    date: z.string(),
    amount: z.string(),
    description: z.string().nullable(),
  })).nullable(),
  nsfComments: z.string(),
  riskScore: riskScoreSchema,
  summary: z.string(),
  flaggedIssues: z.array(z.string()).nullable(),
});

export type DynamicCapitalNSF = z.infer<typeof dynamicCapitalNSFSchema>;

// 4. Long-term Debt Payment Verification
export const dynamicCapitalDebtPaymentsSchema = z.object({
  debtPaymentsIdentified: z.boolean(),
  monthlyDebtPayments: z.array(z.object({
    creditor: z.string(),
    averageMonthlyPayment: z.string(),
    consistency: z.string(),
    applicability: z.string().nullable(),
  })).nullable(),
  totalMonthlyDebtService: z.string().nullable(),
  debtVerificationComments: z.string(),
  riskScore: riskScoreSchema,
  summary: z.string(),
  flaggedIssues: z.array(z.string()).nullable(),
});

export type DynamicCapitalDebtPayments = z.infer<typeof dynamicCapitalDebtPaymentsSchema>;

// Complete Dynamic Capital bank statement findings
export const dynamicCapitalBankStatementFindingsSchema = z.object({
  uploadId: z.string(),
  statementPeriods: z.array(z.string()),
  accountInfo: z.object({
    type: z.literal('dynamic_capital'),
    statementPeriod: z.string(),
    numberOfStatements: z.number(),
    accountHolder: z.string().nullable(),
    accountNumber: z.string().nullable(),
    bankName: z.string().nullable(),
  }),
  findings: z.object({
    deposits: dynamicCapitalDepositsSchema,
    liquidity: dynamicCapitalLiquiditySchema,
    nsfActivity: dynamicCapitalNSFSchema,
    debtPayments: dynamicCapitalDebtPaymentsSchema,
  }),
  overallRiskScore: riskScoreSchema,
  executiveSummary: z.string(),
  extractedDate: z.string(),
});

export type DynamicCapitalBankStatementFindings = z.infer<typeof dynamicCapitalBankStatementFindingsSchema>;

// ===========================================
// Union Type for Any Bank Statement Findings
// ===========================================

export const anyBankStatementFindingsSchema = z.union([
  bankStatementFindingsSchema,
  businessBankStatementFindingsSchema,
  dynamicCapitalBankStatementFindingsSchema,
]);

export type AnyBankStatementFindings = z.infer<typeof anyBankStatementFindingsSchema>;

// ===========================================
// Multi-Document Analysis Wrapper Types
//
// When multiple files of the same type are uploaded, each is extracted
// individually, then the results are wrapped in this structure.
// The `items` array holds the per-document extractions (same type as single-doc).
// The `crossPeriodAnalysis` holds the combined intelligence (trends, anomalies).
//
// These types are used by both the backend (analysis services) and frontend
// (processing dashboard, analysis page) to handle multi-document data.
// ===========================================

/**
 * Generic wrapper for multi-document extraction results.
 * T is the single-document extraction type (e.g., BankStatementFindings).
 * crossPeriodAnalysis is null when only one document was uploaded.
 */
export interface MultiDocumentResult<T> {
  items: T[];
  crossPeriodAnalysis: Record<string, unknown> | null;
}

